import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { lensLabel } from "../lib/lenses.js";
import { buildIndex, kindLabel, search } from "../lib/search.js";

const typing = t => t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

/**
 * The search button in the top bar and the palette it opens (also on / and Ctrl or Cmd+K).
 * Enter opens a result in the current lens; each row's buttons open it in another lens.
 * While the palette is open it takes every key first, so the open lens's shortcuts stay quiet.
 */
export default function SearchPalette({ shell, lens, lenses, open, setOpen }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const lastFocus = useRef(null);
  const D = shell.day;
  const index = useMemo(() => buildIndex(D), [D]);
  const hits = open ? search(index, D, query) : [];
  const cur = Math.min(sel, Math.max(0, hits.length - 1));
  const example = D.areas.slice().sort((a, b) => b.z - a.z)[0]?.name.toLowerCase();

  const show = () => {
    lastFocus.current = document.activeElement;
    setQuery("");
    setSel(0);
    setOpen(true);
  };
  const hide = () => {
    setOpen(false);
    lastFocus.current?.focus?.();
  };
  const choose = r => {
    if (!r) return;
    setOpen(false);
    navigate(shell.link(lens, r.target));
  };

  useEffect(() => {
    const onKey = e => {
      if (open) {
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          hide();
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSel(Math.min(hits.length - 1, cur + 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSel(Math.max(0, cur - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          choose(hits[cur]);
        } else if (e.key === "Tab") {
          e.preventDefault();
          inputRef.current?.focus();
        }
        return;
      }
      const shortcut =
        ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) ||
        (e.key === "/" && !typing(e.target) && !e.ctrlKey && !e.metaKey && !e.altKey);
      if (shortcut) {
        e.preventDefault();
        e.stopPropagation();
        show();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    listRef.current?.querySelector(`#app-pal-r${cur}`)?.scrollIntoView({ block: "nearest" });
  }, [cur, open]);

  const kindOf = r => (r.type === "paper" ? kindLabel(D.paperById[r.id]?.kind) : r.type === "area" ? "Area" : "Tutorial");

  return (
    <>
      <button type="button" className="app-btn app-search" aria-label="Search items, areas and tutorials" onClick={show}>
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.6" />
          <path d="M10.4 10.4 14 14" />
        </svg>
        <span>Search</span>
        <kbd>/</kbd>
      </button>

      {open &&
        createPortal(
          <div
            className="app-pal"
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            onPointerDown={e => {
              if (e.target === e.currentTarget) hide();
            }}
          >
            <div className="app-pal-box">
              <input
                ref={inputRef}
                className="app-pal-q"
                type="search"
                autoComplete="off"
                spellCheck={false}
                placeholder="Search items, areas and tutorials"
                value={query}
                onChange={e => {
                  setQuery(e.target.value);
                  setSel(0);
                }}
                aria-controls="app-pal-res"
                aria-activedescendant={hits.length ? `app-pal-r${cur}` : undefined}
              />
              <p className="app-pal-head">
                {query.trim() ? `${hits.length} match${hits.length === 1 ? "" : "es"}` : "Strongest today"}
              </p>
              <ul className="app-pal-res" id="app-pal-res" role="listbox" ref={listRef}>
                {hits.length === 0 ? (
                  <li className="app-pal-none">
                    Nothing matches “{query.trim()}”.{example ? ` Try an area name, such as ${example}.` : ""}
                  </li>
                ) : (
                  hits.map((r, i) => (
                    <li
                      key={r.id}
                      id={`app-pal-r${i}`}
                      role="option"
                      aria-selected={i === cur}
                      className="app-pal-r"
                      onClick={() => choose(r)}
                      onMouseMove={() => i !== cur && setSel(i)}
                    >
                      <span className="app-pal-k">{kindOf(r)}</span>
                      <span className="app-pal-t">{r.title}</span>
                      <span className="app-pal-a">
                        {lenses
                          .filter(l => l.id !== lens)
                          .map(l => (
                            <Link
                              key={l.id}
                              to={shell.link(l.id, r.target)}
                              title={`Open in ${l.label}`}
                              onClick={e => {
                                e.stopPropagation();
                                setOpen(false);
                              }}
                            >
                              {l.short}
                            </Link>
                          ))}
                      </span>
                      <span className="app-pal-s">{r.sub}</span>
                    </li>
                  ))
                )}
              </ul>
              <p className="app-pal-foot">↑ ↓ to move · Enter opens in {lensLabel(lens)} · the buttons open another lens · Esc closes</p>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
